import React, { useEffect } from 'react';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useForumContext } from '../../../context/ForumContext';
import QuestionCard from './QuestionCard';
import LoadingSkeleton from '../../common/LoadingSkeleton';
import Button from '../../common/Button';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ForumQuestionList = ({ 
  className = ''
}) => {
  const { user } = useAuthContext();
  
  // Get forum state from context
  const {
    questions,
    loading,
    error,
    hasMore,
    loadingMore,
    filterState,
    loadQuestions,
    loadMoreQuestions,
    updateQuestion,
    refreshQuestions,
    currentUserId
  } = useForumContext();

  // Load questions when filter state changes
  useEffect(() => {
    if (user && currentUserId) {
      loadQuestions(currentUserId);
    }
  }, [filterState, user, currentUserId, loadQuestions]);

  // Load more questions
  const handleLoadMore = () => {
    if (!loadingMore && hasMore && currentUserId) {
      loadMoreQuestions(currentUserId);
    }
  };

  // Handle like updates from LikeButton component
  const handleLikeUpdate = (questionId, newLikeCount, isLiked) => {
    updateQuestion(questionId, { likeCount: newLikeCount });
  };

  // Retry function for error states
  const handleRetry = () => {
    if (currentUserId) {
      refreshQuestions(currentUserId);
    }
  };

  // Loading skeleton - Mobile optimized
  if (loading && questions.length === 0) {
    return (
      <div className={`space-y-0 sm:space-y-1 divide-y divide-gray-100 dark:divide-gray-700 sm:divide-none ${className}`}>
        {[...Array(5)].map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 px-3 py-3 sm:px-4 sm:py-4 border-b border-gray-100 dark:border-gray-700 sm:rounded-lg sm:shadow-sm sm:border sm:border-gray-200 sm:dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <LoadingSkeleton className="w-6 h-6 sm:w-8 sm:h-8 rounded-full" />
              <div className="flex items-center space-x-1">
                <LoadingSkeleton className="h-3 w-16 sm:h-4 sm:w-20" />
                <LoadingSkeleton className="h-3 w-12 sm:h-4 sm:w-16" />
              </div>
            </div>
            <LoadingSkeleton className="h-5 w-3/4 mb-2 sm:h-6" />
            <LoadingSkeleton className="h-4 w-full mb-2 sm:h-5" />
            <LoadingSkeleton className="h-4 w-2/3 mb-3 sm:h-5" />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <LoadingSkeleton className="h-3 w-12 sm:h-4 sm:w-16" />
                <LoadingSkeleton className="h-3 w-12 sm:h-4 sm:w-16" />
              </div>
              <LoadingSkeleton className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && questions.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 max-w-md mx-auto">
          <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Unable to load questions
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <Button
            variant="outline"
            onClick={handleRetry}
            className="inline-flex items-center"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && questions.length === 0) {
    const isFiltered = filterState.searchTerm || filterState.tags.length > 0 || filterState.showMyPostsOnly;
    
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 max-w-md mx-auto">
          <div className="h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">💬</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {isFiltered ? 'No questions match your filters' : 'No questions yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {isFiltered 
              ? 'Try adjusting your search terms or filters to see more results.'
              : 'Be the first to start a discussion in the community!'
            }
          </p>
          {isFiltered && (
            <Button
              variant="outline"
              onClick={handleRetry}
              className="inline-flex items-center"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Questions list
  return (
    <div className={`${className}`}>
      {/* Mobile: List view without cards */}
      <div className="sm:hidden space-y-0 divide-y divide-gray-100 dark:divide-gray-700">
        {questions.map((question) => (
          <QuestionCard
            key={question.id}
            question={question}
            onLike={handleLikeUpdate}
            className="border-0 rounded-none shadow-none"
          />
        ))}
      </div>

      {/* Desktop: Card view */}
      <div className="hidden sm:block space-y-4">
        {questions.map((question) => (
          <QuestionCard
            key={question.id}
            question={question}
            onLike={handleLikeUpdate}
            className="rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="min-w-[120px]"
          >
            {loadingMore ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="mt-4 space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 px-3 py-3 sm:px-4 sm:py-4 border border-gray-200 dark:border-gray-700 rounded-lg animate-pulse">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex items-center space-x-1">
                  <div className="h-3 w-16 sm:h-4 sm:w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 w-12 sm:h-4 sm:w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
              <div className="h-5 w-3/4 mb-2 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 w-full mb-2 sm:h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForumQuestionList;